/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import LegalPageLayout from './LegalPageLayout';
import { useConsent } from '../../context/ConsentContext';

const Row = ({ name, provider, purpose, category, duration, page }) => (
  <tr className="border-b border-white/10">
    <td className="py-2 pr-3 align-top"><code className="text-xs text-pink-300">{name}</code></td>
    <td className="py-2 pr-3 align-top text-sm">{provider}</td>
    <td className="py-2 pr-3 align-top text-sm">{purpose}</td>
    <td className="py-2 pr-3 align-top"><span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border border-white/10">{category}</span></td>
    <td className="py-2 pr-3 align-top text-xs text-zinc-400">{duration}</td>
    <td className="py-2 align-top text-xs text-zinc-400">{page}</td>
  </tr>
);

const CookiePolicyEN = () => {
  const { openPreferences } = useConsent();
  return (
    <LegalPageLayout
      title="Cookie Policy"
      subtitle="Inventory and purpose of cookies, trackers and storage used on body-up.ch."
      lastUpdated="Last update: June 21, 2026"
      seoTitle="Cookie Policy | Body-Up Personal Training Nyon"
      seoPath="/cookie-policy"
    >
      <h2>1. What is a cookie?</h2>
      <p>A cookie is a small file stored by your browser when visiting a site. The term cookie here also includes equivalent technologies: <code>localStorage</code>, <code>sessionStorage</code> and pixels.</p>

      <h2>2. Categories</h2>
      <ul>
        <li><strong>Necessary</strong>: essential for the site to work (authentication, language, consent choice). Always on.</li>
        <li><strong>Analytics</strong>: audience measurement. <strong>Off by default.</strong> No analytics tool is active today; your choice here will only apply when Body-Up enables one.</li>
        <li><strong>Marketing</strong>: advertising and conversion. <strong>Off by default.</strong> Likewise, no marketing tool is active today.</li>
      </ul>

      <h2>3. Current inventory</h2>
      <p>This inventory is intentionally transparent: it only lists what is <em>actually</em> loaded today, without inventing fictitious providers.</p>

      <h3>3.1 Strictly necessary cookies / localStorage</h3>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-left">
          <thead className="text-xs uppercase text-zinc-500 border-b border-white/10"><tr>
            <th className="py-2 pr-3">Name</th>
            <th className="py-2 pr-3">Provider</th>
            <th className="py-2 pr-3">Purpose</th>
            <th className="py-2 pr-3">Category</th>
            <th className="py-2 pr-3">Duration</th>
            <th className="py-2">Page(s)</th>
          </tr></thead>
          <tbody>
            <Row name="bodyup_consent_v1" provider="Body-Up" purpose="Stores your consent choice (categories, version, date)." category="Necessary" duration="12 months" page="All" />
            <Row name="bodyup_client_token" provider="Body-Up" purpose="JWT session token after client login." category="Necessary" duration="Session / 7 days" page="/espace-client, /mon-espace" />
            <Row name="bodyup-language" provider="Body-Up" purpose="Remembers your language (FR/EN)." category="Necessary" duration="12 months" page="All" />
            <Row name="bodyup_guide_* / bodyup_tour_*" provider="Body-Up" purpose="Remembers viewed interactive guides and tours." category="Necessary" duration="12 months" page="/mon-espace" />
            <Row name="bodyup_tooltip_*" provider="Body-Up" purpose="Remembers already seen tooltips to avoid repetition." category="Necessary" duration="12 months" page="/mon-espace" />
          </tbody>
        </table>
      </div>

      <h3>3.2 Third-party cookies loaded without consent (technical)</h3>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-left">
          <thead className="text-xs uppercase text-zinc-500 border-b border-white/10"><tr>
            <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Provider</th><th className="py-2 pr-3">Purpose</th><th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Duration</th><th className="py-2">Page(s)</th>
          </tr></thead>
          <tbody>
            <Row name="Stripe (__stripe_*)" provider="Stripe" purpose="Payment checkout security. Loaded only on payment page." category="Necessary" duration="≤ 1 year" page="Payment / Stripe Checkout" />
          </tbody>
        </table>
      </div>

      <p className="mb-3 text-zinc-400 text-sm">
        <strong>Note:</strong> Fonts are now <strong>self-hosted locally</strong> on our servers (no more connection to Google Fonts).
      </p>

      <h3>3.3 Analytics and marketing cookies</h3>
      <p><strong>No analytics or marketing tool is currently loaded on body-up.ch.</strong> Google Analytics (GA4) and Meta Pixel are not active. This section will be updated when those tools are enabled, with exact provider, duration and purpose. No loading will occur without your prior consent.</p>

      <h3>3.4 Embedded external content</h3>
      <ul>
        <li><strong>Pure Informatique booking system</strong> (book.pure-informatique.com): used for appointment booking. Opened in a new window from "Book" buttons.</li>
        <li><strong>Social links</strong> (Instagram, Facebook, LinkedIn): no cookie is set until you click the link.</li>
        <li><strong>YouTube / Vimeo</strong>: not directly embedded today. Any future embed will be blocked until you accept the marketing category.</li>
      </ul>

      <h2>4. Modify or withdraw consent</h2>
      <p>You can modify or withdraw your consent at any time:</p>
      <ul>
        <li>Via the "Manage cookies" link in the footer of every page.</li>
        <li>By clicking the button below.</li>
      </ul>
      <button
        type="button"
        onClick={openPreferences}
        className="not-prose inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:opacity-90 transition-opacity"
        data-testid="cookie-policy-manage-btn"
      >
        Manage my cookie preferences
      </button>

      <h2>5. Policy updates</h2>
      <p>In case of an important change (new provider, new purpose, new cookies), a new consent will be requested and the policy version will be incremented.</p>

      <h2>6. Contact</h2>
      <p>For any question about cookies: <a href="mailto:info@body-up.ch">info@body-up.ch</a>.</p>
    </LegalPageLayout>
  );
};

export default CookiePolicyEN;
